import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Trash2, PackagePlus, BookUp2, CopyPlus, X, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { errMsg, fmtMoney, fmtNumber } from '../../lib/format';

const NumCell = ({ value, onCommit, disabled, testid, width = 'w-24', step = '0.01' }) => {
  const [val, setVal] = useState(value);
  React.useEffect(() => setVal(value), [value]);
  return (
    <Input
      type="number" step={step} value={val ?? ''} disabled={disabled}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => { if (String(val) !== String(value)) onCommit(Number(val)); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
      className={`h-8 ${width} text-right font-mono text-[12.5px] px-2`}
      data-testid={testid}
    />
  );
};

const MonedaCell = ({ value, onCommit, disabled, testid }) => (
  <Select value={value || 'MXN'} onValueChange={onCommit} disabled={disabled}>
    <SelectTrigger className="h-8 w-[76px] text-[12px]" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent><SelectItem value="MXN">MXN</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
  </Select>
);

const CatalogAdd = ({ type, scope, onAdd, disabled }) => {
  const [selId, setSelId] = useState('');
  const [cantidad, setCantidad] = useState(1);
  const { data } = useQuery({
    queryKey: ['catalog', type, scope, ''],
    queryFn: async () => (await api.get(`/catalog/${type}`, { params: { scope, limit: 300 } })).data,
  });
  const items = data?.items || [];
  const label = (c) => type === 'labor'
    ? `${c.numero} · ${c.categoria} · $${fmtNumber(c.salario_base)}`
    : `${c.numero} · ${c.descripcion} · ${fmtMoney(c.costo_unitario, c.moneda)}`;
  return (
    <div className="flex flex-wrap items-end gap-2 p-3 rounded-lg border border-dashed border-border">
      <div className="flex-1 min-w-[220px] space-y-1">
        <Label className="text-[11px] text-muted-foreground">Agregar del catálogo ({scope})</Label>
        <Select value={selId} onValueChange={setSelId} disabled={disabled}>
          <SelectTrigger className="h-8" data-testid={`apu-catalog-select-${type}`}><SelectValue placeholder="Buscar insumo..." /></SelectTrigger>
          <SelectContent className="max-h-64">
            {items.map((c) => <SelectItem key={c.id} value={c.id}>{label(c)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {type !== 'labor' && (
        <div className="space-y-1">
          <Label className="text-[11px] text-muted-foreground">Cantidad</Label>
          <Input type="number" step="0.0001" value={cantidad} onChange={(e) => setCantidad(e.target.value)} className="h-8 w-24 font-mono text-right" data-testid={`apu-catalog-cantidad-${type}`} disabled={disabled} />
        </div>
      )}
      <Button size="sm" className="h-8" disabled={!selId || disabled} onClick={() => { onAdd(selId, Number(cantidad)); setSelId(''); }} data-testid={`apu-catalog-add-${type}`}>
        <PackagePlus size={14} className="mr-1.5" /> Agregar
      </Button>
    </div>
  );
};

const ManualAdd = ({ type, onAdd, disabled }) => {
  const empty = type === 'labor'
    ? { categoria: '', salario_base: 0, factor_salario_real: 1.45, rendimiento: 1, moneda: 'MXN' }
    : { descripcion: '', unidad: 'pza', cantidad: 1, costo_unitario: 0, moneda: 'MXN' };
  const [form, setForm] = useState(empty);
  const valid = type === 'labor' ? form.categoria : form.descripcion;
  const set = (k, num) => (e) => setForm((f) => ({ ...f, [k]: num ? Number(e.target.value) : e.target.value }));
  return (
    <div className="flex flex-wrap items-end gap-2 p-3 rounded-lg border border-dashed border-border">
      <div className="flex-1 min-w-[180px] space-y-1">
        <Label className="text-[11px] text-muted-foreground">{type === 'labor' ? 'Categoría (nueva captura → se registra en catálogo)' : 'Descripción (nueva captura → se registra en catálogo)'}</Label>
        <Input value={type === 'labor' ? form.categoria : form.descripcion} onChange={set(type === 'labor' ? 'categoria' : 'descripcion')} className="h-8" data-testid={`apu-manual-desc-${type}`} disabled={disabled} />
      </div>
      {type === 'labor' ? (
        <>
          <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">Salario base</Label><Input type="number" step="0.01" value={form.salario_base} onChange={set('salario_base', true)} className="h-8 w-24 font-mono text-right" data-testid="apu-manual-salario" disabled={disabled} /></div>
          <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">Factor</Label><Input type="number" step="0.0001" value={form.factor_salario_real} onChange={set('factor_salario_real', true)} className="h-8 w-20 font-mono text-right" data-testid="apu-manual-factor" disabled={disabled} /></div>
          <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">Rendimiento</Label><Input type="number" step="0.0001" value={form.rendimiento} onChange={set('rendimiento', true)} className="h-8 w-24 font-mono text-right" data-testid="apu-manual-rendimiento" disabled={disabled} /></div>
        </>
      ) : (
        <>
          <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">Unidad</Label><Input value={form.unidad} onChange={set('unidad')} className="h-8 w-20" data-testid={`apu-manual-unidad-${type}`} disabled={disabled} /></div>
          <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">Cantidad</Label><Input type="number" step="0.0001" value={form.cantidad} onChange={set('cantidad', true)} className="h-8 w-24 font-mono text-right" data-testid={`apu-manual-cantidad-${type}`} disabled={disabled} /></div>
          <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">Costo unit.</Label><Input type="number" step="0.01" value={form.costo_unitario} onChange={set('costo_unitario', true)} className="h-8 w-24 font-mono text-right" data-testid={`apu-manual-costo-${type}`} disabled={disabled} /></div>
        </>
      )}
      <div className="space-y-1">
        <Label className="text-[11px] text-muted-foreground">Moneda</Label>
        <Select value={form.moneda} onValueChange={(v) => setForm((f) => ({ ...f, moneda: v }))} disabled={disabled}>
          <SelectTrigger className="h-8 w-[76px] text-[12px]"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="MXN">MXN</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
        </Select>
      </div>
      <Button size="sm" variant="outline" className="h-8" disabled={!valid || disabled} onClick={() => { onAdd(form); setForm(empty); }} data-testid={`apu-manual-add-${type}`}>
        <Plus size={14} className="mr-1.5" /> Capturar
      </Button>
    </div>
  );
};

/**
 * Diálogo APU por concepto: 3 tablas (Materiales / Mano de Obra / Equipo).
 */
export const ApuDialog = ({ open, onOpenChange, concept, quotation, frozen, editor }) => {
  const { canDelete } = useAuth();
  const queryClient = useQueryClient();
  const scope = quotation?.quotation_type === 'pv' ? 'pv' : 'prefab';
  const moneda = quotation?.moneda || 'MXN';
  const [dirty, setDirty] = useState(false);
  const [syncPromptOpen, setSyncPromptOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Reset del estado "modificado" al abrir para un concepto
  React.useEffect(() => {
    if (open) setDirty(false);
  }, [open, concept?.id]);

  // Concepto siempre fresco desde la cotización cacheada
  const liveConcept = useMemo(() => {
    if (!concept || !quotation?.groups) return concept;
    for (const g of quotation.groups) {
      const found = (g.concepts || []).find((c) => c.id === concept.id);
      if (found) return found;
    }
    return concept;
  }, [concept, quotation]);

  if (!liveConcept) return null;

  const markDirty = () => setDirty(true);
  const commitItem = (type, itemId, field) => (v) => { markDirty(); editor.updateApuItem.mutate({ type, itemId, patch: { [field]: v } }); };
  const addManual = (type) => (form) => { markDirty(); editor.addApuItem.mutate({ type, payload: { concept_id: liveConcept.id, ...form } }); };
  const addCatalog = (type) => (catalogId, cantidad) => { markDirty(); editor.addApuFromCatalog.mutate({ type, payload: { concept_id: liveConcept.id, catalog_id: catalogId, cantidad } }); };
  const removeItem = (type, itemId) => { markDirty(); editor.deleteApuItem.mutate({ type, itemId }); };

  const closeAll = () => {
    setSyncPromptOpen(false);
    setDirty(false);
    onOpenChange(false);
  };

  // Al cerrar con cambios: preguntar si se guarda en catálogo
  const handleDialogChange = (v) => {
    if (!v && dirty && !frozen) {
      setSyncPromptOpen(true);
      return;
    }
    onOpenChange(v);
  };

  const syncToCatalog = async (mode) => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/concepts/${liveConcept.id}/sync-to-catalog`, { mode });
      toast.success(mode === 'update'
        ? `Concepto ${data.numero} actualizado en el catálogo`
        : `Guardado en catálogo como copia nueva: ${data.numero}`);
      queryClient.invalidateQueries({ queryKey: ['catalog'] });
      queryClient.invalidateQueries({ queryKey: ['quotation-full'] });
      closeAll();
    } catch (e) {
      toast.error(errMsg(e, 'No se pudo guardar en el catálogo'));
    } finally {
      setSyncing(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="apu-dialog">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2 text-base">
            APU · {liveConcept.numero_concepto} · {liveConcept.descripcion}
            <span className="text-[12px] font-normal text-muted-foreground">({liveConcept.unidad})</span>
            {liveConcept.catalog_numero && (
              <span className="font-mono text-[11px] font-normal px-1.5 py-0.5 rounded border border-border text-muted-foreground" data-testid="apu-catalog-link">
                {liveConcept.catalog_numero}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="materials">
          <TabsList>
            <TabsTrigger value="materials" data-testid="apu-tab-materiales">Materiales ({(liveConcept.materials || []).length})</TabsTrigger>
            <TabsTrigger value="labor" data-testid="apu-tab-mano-obra">Mano de Obra ({(liveConcept.labor || []).length})</TabsTrigger>
            <TabsTrigger value="equipment" data-testid="apu-tab-equipo">Equipo ({(liveConcept.equipment || []).length})</TabsTrigger>
          </TabsList>

          {/* MATERIALES */}
          <TabsContent value="materials" className="space-y-3">
            <Table data-testid="apu-materials-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="th-label">Catálogo</TableHead>
                  <TableHead className="th-label min-w-[200px]">Descripción</TableHead>
                  <TableHead className="th-label">Unidad</TableHead>
                  <TableHead className="th-label">Cantidad</TableHead>
                  <TableHead className="th-label text-right">Costo unit.</TableHead>
                  <TableHead className="th-label">Moneda</TableHead>
                  <TableHead className="th-label text-right">Total</TableHead>
                  <TableHead className="th-label w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(liveConcept.materials || []).length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">Sin materiales</TableCell></TableRow>
                )}
                {(liveConcept.materials || []).map((m) => (
                  <TableRow key={m.id} className="hover:bg-background/40" data-testid={`apu-material-row-${m.id}`}>
                    <TableCell className="font-mono text-[12px] text-muted-foreground">{m.catalog_numero || '—'}</TableCell>
                    <TableCell className="text-[13px]">{m.descripcion}</TableCell>
                    <TableCell className="text-[12.5px] text-muted-foreground">{m.unidad}</TableCell>
                    <TableCell><NumCell value={m.cantidad} step="0.0001" disabled={frozen} testid={`material-cantidad-${m.id}`} onCommit={commitItem('materials', m.id, 'cantidad')} /></TableCell>
                    <TableCell className="text-right"><NumCell value={m.costo_unitario} disabled={frozen} testid={`material-costo-${m.id}`} onCommit={commitItem('materials', m.id, 'costo_unitario')} /></TableCell>
                    <TableCell><MonedaCell value={m.moneda} disabled={frozen} testid={`material-moneda-${m.id}`} onCommit={commitItem('materials', m.id, 'moneda')} /></TableCell>
                    <TableCell className="money text-[13px]">{fmtMoney(m.total, m.moneda)}</TableCell>
                    <TableCell>
                      {!frozen && canDelete && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => removeItem('materials', m.id)} data-testid={`delete-material-${m.id}`}>
                          <Trash2 size={13} />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {!frozen && (
              <>
                <CatalogAdd type="materials" scope={scope} onAdd={addCatalog('materials')} />
                <ManualAdd type="materials" onAdd={addManual('materials')} />
              </>
            )}
            <div className="flex justify-end text-[13px]">
              <span className="text-muted-foreground mr-3">Subtotal materiales:</span>
              <span className="money font-medium" data-testid="apu-materials-subtotal">{fmtMoney(liveConcept.materials_cost, moneda)}</span>
            </div>
          </TabsContent>

          {/* MANO DE OBRA */}
          <TabsContent value="labor" className="space-y-3">
            <Table data-testid="apu-labor-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="th-label">Catálogo</TableHead>
                  <TableHead className="th-label min-w-[160px]">Categoría</TableHead>
                  <TableHead className="th-label text-right">Salario base</TableHead>
                  <TableHead className="th-label">Factor</TableHead>
                  <TableHead className="th-label text-right">Salario real</TableHead>
                  <TableHead className="th-label">Rendimiento</TableHead>
                  <TableHead className="th-label">Moneda</TableHead>
                  <TableHead className="th-label text-right">Costo</TableHead>
                  <TableHead className="th-label w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(liveConcept.labor || []).length === 0 && (
                  <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-6">Sin mano de obra</TableCell></TableRow>
                )}
                {(liveConcept.labor || []).map((l) => (
                  <TableRow key={l.id} className="hover:bg-background/40" data-testid={`apu-labor-row-${l.id}`}>
                    <TableCell className="font-mono text-[12px] text-muted-foreground">{l.catalog_numero || '—'}</TableCell>
                    <TableCell className="text-[13px]">{l.categoria}</TableCell>
                    <TableCell className="text-right"><NumCell value={l.salario_base} disabled={frozen} testid={`labor-salario-${l.id}`} onCommit={commitItem('labor', l.id, 'salario_base')} /></TableCell>
                    <TableCell><NumCell value={l.factor_salario_real} step="0.0001" width="w-20" disabled={frozen} testid={`labor-factor-${l.id}`} onCommit={commitItem('labor', l.id, 'factor_salario_real')} /></TableCell>
                    <TableCell className="money text-[13px] text-muted-foreground">{fmtMoney(l.salario_real, l.moneda)}</TableCell>
                    <TableCell><NumCell value={l.rendimiento} step="0.0001" width="w-20" disabled={frozen} testid={`labor-rendimiento-${l.id}`} onCommit={commitItem('labor', l.id, 'rendimiento')} /></TableCell>
                    <TableCell><MonedaCell value={l.moneda} disabled={frozen} testid={`labor-moneda-${l.id}`} onCommit={commitItem('labor', l.id, 'moneda')} /></TableCell>
                    <TableCell className="money text-[13px]">{fmtMoney(l.costo_total, l.moneda)}</TableCell>
                    <TableCell>
                      {!frozen && canDelete && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => removeItem('labor', l.id)} data-testid={`delete-labor-${l.id}`}>
                          <Trash2 size={13} />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {!frozen && (
              <>
                <CatalogAdd type="labor" scope={scope} onAdd={(id) => addCatalog('labor')(id, 1)} />
                <ManualAdd type="labor" onAdd={addManual('labor')} />
              </>
            )}
            <div className="flex justify-end text-[13px]">
              <span className="text-muted-foreground mr-3">Subtotal mano de obra:</span>
              <span className="money font-medium" data-testid="apu-labor-subtotal">{fmtMoney(liveConcept.labor_cost, moneda)}</span>
            </div>
          </TabsContent>

          {/* EQUIPO */}
          <TabsContent value="equipment" className="space-y-3">
            <Table data-testid="apu-equipment-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="th-label">Catálogo</TableHead>
                  <TableHead className="th-label min-w-[200px]">Descripción</TableHead>
                  <TableHead className="th-label">Tipo</TableHead>
                  <TableHead className="th-label">Cantidad</TableHead>
                  <TableHead className="th-label text-right">Costo unit.</TableHead>
                  <TableHead className="th-label">Moneda</TableHead>
                  <TableHead className="th-label text-right">Total</TableHead>
                  <TableHead className="th-label w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(liveConcept.equipment || []).length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">Sin equipo</TableCell></TableRow>
                )}
                {(liveConcept.equipment || []).map((e) => (
                  <TableRow key={e.id} className="hover:bg-background/40" data-testid={`apu-equipment-row-${e.id}`}>
                    <TableCell className="font-mono text-[12px] text-muted-foreground">{e.catalog_numero || '—'}</TableCell>
                    <TableCell className="text-[13px]">{e.descripcion}</TableCell>
                    <TableCell className="text-[12.5px] text-muted-foreground">{e.tipo || '—'}</TableCell>
                    <TableCell><NumCell value={e.cantidad} step="0.0001" disabled={frozen} testid={`equipment-cantidad-${e.id}`} onCommit={commitItem('equipment', e.id, 'cantidad')} /></TableCell>
                    <TableCell className="text-right"><NumCell value={e.costo_unitario} disabled={frozen} testid={`equipment-costo-${e.id}`} onCommit={commitItem('equipment', e.id, 'costo_unitario')} /></TableCell>
                    <TableCell><MonedaCell value={e.moneda} disabled={frozen} testid={`equipment-moneda-${e.id}`} onCommit={commitItem('equipment', e.id, 'moneda')} /></TableCell>
                    <TableCell className="money text-[13px]">{fmtMoney(e.total, e.moneda)}</TableCell>
                    <TableCell>
                      {!frozen && canDelete && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => removeItem('equipment', e.id)} data-testid={`delete-equipment-${e.id}`}>
                          <Trash2 size={13} />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {!frozen && (
              <>
                <CatalogAdd type="equipment" scope={scope} onAdd={addCatalog('equipment')} />
                <ManualAdd type="equipment" onAdd={addManual('equipment')} />
              </>
            )}
            <div className="flex justify-end text-[13px]">
              <span className="text-muted-foreground mr-3">Subtotal equipo:</span>
              <span className="money font-medium" data-testid="apu-equipment-subtotal">{fmtMoney(liveConcept.equipment_cost, moneda)}</span>
            </div>
          </TabsContent>
        </Tabs>

        <Separator />

        {/* Cascada del concepto */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[13px]" data-testid="apu-concept-cascade">
          <div className="rounded-lg border border-border p-3">
            <div className="text-[11px] text-muted-foreground uppercase tracking-wide">Costo directo</div>
            <div className="money font-semibold mt-1">{fmtMoney(liveConcept.direct_cost, moneda)}</div>
          </div>
          <div className="rounded-lg border border-border p-3">
            <div className="text-[11px] text-muted-foreground uppercase tracking-wide">+ Ind. {fmtNumber(liveConcept.indirect_percentage, 2)}% · Fin. {fmtNumber(liveConcept.financing_percentage, 2)}% · Util. {fmtNumber(liveConcept.utility_percentage, 2)}%</div>
            <div className="money font-semibold mt-1">{fmtMoney(liveConcept.calculated_unit_price, moneda)}</div>
          </div>
          <div className="rounded-lg border border-border p-3">
            <div className="text-[11px] text-muted-foreground uppercase tracking-wide">Cantidad</div>
            <div className="money font-semibold mt-1">{fmtNumber(liveConcept.cantidad, 2)} {liveConcept.unidad}</div>
          </div>
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
            <div className="text-[11px] text-muted-foreground uppercase tracking-wide">Total concepto</div>
            <div className="money font-semibold text-primary mt-1" data-testid="apu-concept-total">{fmtMoney(liveConcept.calculated_total, moneda)}</div>
          </div>
        </div>

        {!frozen && (
          <DialogFooter className="mt-1">
            <Button variant="outline" size="sm" onClick={() => setSyncPromptOpen(true)} data-testid="apu-save-to-catalog-button">
              <BookUp2 size={14} className="mr-1.5" /> Guardar en catálogo
            </Button>
            <Button size="sm" onClick={() => handleDialogChange(false)} data-testid="apu-close-button">Cerrar</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>

    {/* Prompt: guardar cambios del concepto en el catálogo */}
    <Dialog open={syncPromptOpen} onOpenChange={(v) => { if (!v) setSyncPromptOpen(false); }}>
      <DialogContent className="max-w-md" data-testid="sync-catalog-prompt">
        <DialogHeader>
          <DialogTitle className="text-base">¿Guardar este concepto en el catálogo?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {liveConcept.catalog_numero
            ? <>Este concepto está vinculado a <span className="font-mono text-foreground">{liveConcept.catalog_numero}</span>. Puedes actualizar el concepto del catálogo con estos cambios o crear una copia nueva versionada.</>
            : 'Este concepto aún no está en el catálogo. Puedes registrarlo para reutilizarlo en otras cotizaciones.'}
        </p>
        <div className="space-y-2">
          {liveConcept.catalog_numero && (
            <Button className="w-full justify-start" variant="outline" disabled={syncing} onClick={() => syncToCatalog('update')} data-testid="sync-update-button">
              {syncing ? <Loader2 size={14} className="mr-2 animate-spin" /> : <BookUp2 size={14} className="mr-2" />}
              Actualizar {liveConcept.catalog_numero} en el catálogo
            </Button>
          )}
          <Button className="w-full justify-start" variant="outline" disabled={syncing} onClick={() => syncToCatalog('new')} data-testid="sync-new-button">
            {syncing ? <Loader2 size={14} className="mr-2 animate-spin" /> : <CopyPlus size={14} className="mr-2" />}
            {liveConcept.catalog_numero ? 'Crear copia nueva en el catálogo' : 'Registrar como concepto nuevo'}
          </Button>
          <Button className="w-full justify-start" variant="ghost" disabled={syncing} onClick={closeAll} data-testid="sync-skip-button">
            <X size={14} className="mr-2" /> Solo en esta cotización (no guardar en catálogo)
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};
